import axios from 'axios';
import { getApiUrl } from '../../../config/api';

export class UsuariosController {
  constructor() {
    this.usuarios = [];
    this.loading = false;
    this.error = null;
  }

  /**
   * Inicializa el controlador cargando la lista de usuarios
   */
  async init() {
    await this.fetchUsuarios();
  }

  /**
   * Obtiene la lista de usuarios desde el servidor
   */
  async fetchUsuarios() {
    this.loading = true;
    this.error = null;

    try {
      const response = await axios.post(getApiUrl('/api/Usuario/listar'));
      this.usuarios = response.data || [];
      this.loading = false;
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      this.error = 'No se pudieron cargar los usuarios. Por favor, intente nuevamente.';
      this.loading = false;
      throw error;
    }
  }

  /**
   * Elimina un usuario del sistema
   * @param {string} usuarioId - ID del usuario a eliminar
   * @returns {Promise} Promise que resuelve cuando el usuario es eliminado
   */
  async deleteUsuario(usuarioId) {
    if (!usuarioId) {
      throw new Error('ID de usuario requerido');
    }

    try {
      await axios.post(
        getApiUrl('/api/Usuario/borrar'), 
        { UsuarioId: usuarioId }, 
        { 
          headers: { 
            'Content-Type': 'application/json' 
          }
        }
      );

      // Recargar la lista después de eliminar
      await this.fetchUsuarios();
      
      return {
        success: true,
        message: 'Usuario eliminado correctamente.'
      };
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      throw new Error('No se pudo eliminar el usuario. Por favor, intente nuevamente.');
    }
  }

  /**
   * Confirma la eliminación de un usuario
   * @param {string} usuarioId - ID del usuario a eliminar
   * @param {string} usuarioNombre - Nombre del usuario para mostrar en la confirmación
   * @returns {boolean} true si el usuario confirma la eliminación
   */
  confirmDelete(usuarioId, usuarioNombre = null) {
    const displayName = usuarioNombre || usuarioId;
    return confirm(`¿Está seguro que desea eliminar el usuario "${displayName}"?`);
  }

  /**
   * Formatea una fecha para mostrar en la interfaz
   * @param {string} dateString - Fecha en formato string
   * @returns {string} Fecha formateada o '-' si no hay fecha
   */
  formatDate(dateString) {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return '-';
    }
  }

  /**
   * Filtra usuarios por criterio de búsqueda
   * @param {string} searchTerm - Término de búsqueda
   * @returns {Array} Lista de usuarios filtrados
   */
  filterUsuarios(searchTerm) {
    if (!searchTerm || searchTerm.trim() === '') {
      return this.usuarios;
    }

    const term = searchTerm.toLowerCase().trim();
    return this.usuarios.filter(usuario => 
      (usuario.UsuarioId && usuario.UsuarioId.toLowerCase().includes(term)) ||
      (usuario.Nombre && usuario.Nombre.toLowerCase().includes(term)) ||
      (usuario.Email && usuario.Email.toLowerCase().includes(term))
    );
  }

  /**
   * Obtiene la lista de usuarios
   * @returns {Array} Lista de usuarios
   */
  getUsuarios() {
    return [...this.usuarios];
  }

  /**
   * Obtiene el estado de carga
   * @returns {boolean} true si está cargando
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Obtiene el error actual
   * @returns {string|null} Mensaje de error o null
   */
  getError() {
    return this.error;
  }

  /**
   * Obtiene el número total de usuarios
   * @returns {number} Número de usuarios
   */
  getTotalUsuarios() {
    return this.usuarios.length;
  }

  /**
   * Obtiene el número de usuarios activos
   * @returns {number} Número de usuarios activos
   */
  getActiveUsuarios() {
    return this.usuarios.filter(usuario => usuario.EsActivo).length;
  }

  /**
   * Obtiene el número de usuarios inactivos
   * @returns {number} Número de usuarios inactivos
   */
  getInactiveUsuarios() {
    return this.usuarios.filter(usuario => !usuario.EsActivo).length;
  }

  /**
   * Limpia el error actual
   */
  clearError() {
    this.error = null;
  }

  /**
   * Refresca la lista de usuarios
   */
  async refresh() {
    await this.fetchUsuarios();
  }
}

// Exportar una instancia por defecto para uso directo
export default new UsuariosController();